import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import logoImage from '../images/skullture_white.png';
import { 
  auth, db,
  chaptersCollection, manifestoLinesCollection, sectionsCollection, usersCollection,
  getDocs, addDoc, updateDoc, deleteDoc, doc, query, where, orderBy,
  signInWithEmailAndPassword, onAuthStateChanged, signOut,
  createUserWithEmailAndPassword, sendEmailVerification
} from '../firebase/config';
import CustomCursor from '../components/CustomCursor';
import './AdminPage.css';

const AdminPage = () => {
  const navigate = useNavigate();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [currentAdmin, setCurrentAdmin] = useState(null);
  const [loginEmail, setLoginEmail] = useState('');
  const [loginPassword, setLoginPassword] = useState('');
  const [loginError, setLoginError] = useState('');
  const [loginLoading, setLoginLoading] = useState(false);

  // Data states
  const [chapters, setChapters] = useState([]);
  const [manifestoLines, setManifestoLines] = useState([]);
  const [adminUsers, setAdminUsers] = useState([]);
  const [regularUsers, setRegularUsers] = useState([]);
  const [loading, setLoading] = useState(true);

  // Active tab
  const [activeTab, setActiveTab] = useState('chapters');

  // Chapter form
  const [showChapterModal, setShowChapterModal] = useState(false);
  const [editingChapter, setEditingChapter] = useState(null);
  const [chapterForm, setChapterForm] = useState({
    name: '', tag: '', description: '', fullDescription: '',
    status: 'active', order: 0, price: 499,
    imageFile: null, imagePreview: '', imageData: ''
  });

  // Manifesto form
  const [showManifestoModal, setShowManifestoModal] = useState(false);
  const [editingManifesto, setEditingManifesto] = useState(null);
  const [manifestoForm, setManifestoForm] = useState({ text: '', order: 0 });

  // About sections
  const [aboutSections, setAboutSections] = useState([]);
  const [showAboutModal, setShowAboutModal] = useState(false);
  const [editingAbout, setEditingAbout] = useState(null);
  const [aboutForm, setAboutForm] = useState({ title: '', content: '', icon: '⛧', order: 0 });

  // Top Bar 1
  const [topBar1Content, setTopBar1Content] = useState('');
  const [showTopBar1Modal, setShowTopBar1Modal] = useState(false);
  const [topBar1Text, setTopBar1Text] = useState('');

  // Top Bar 2
  const [topBar2Content, setTopBar2Content] = useState('');
  const [showTopBar2Modal, setShowTopBar2Modal] = useState(false);
  const [topBar2Text, setTopBar2Text] = useState('');

  // Hero content (with image)
  const [heroContent, setHeroContent] = useState({
    badge: '/// CHAPTER ZERO ///',
    title: 'WEAR THE',
    titleHighlight: 'OCCULT',
    titleLine2: 'STRUCTURE',
    subtitle: 'Restricted archive of transmitive garments. Each artifact carries a hidden frequency.',
    buttonPrimary: 'EXPLORE CHAPTERS',
    buttonSecondary: 'VIEW MANIFESTO',
    imageData: ''
  });
  const [showHeroModal, setShowHeroModal] = useState(false);
  const [heroForm, setHeroForm] = useState({ ...heroContent });
  const [heroImageFile, setHeroImageFile] = useState(null);
  const [heroImagePreview, setHeroImagePreview] = useState('');

  // Footer content
  const [footerContent, setFooterContent] = useState({
    tagline: 'Vignex Skullture',
    contactEmail: 'muttannapd2456@gmail.com',
    contactLocation: 'Bangalore',
    copyright: '© SKULLTURE — ARCHIVE ACCESS LEVEL: PUBLIC'
  });
  const [showFooterModal, setShowFooterModal] = useState(false);
  const [footerForm, setFooterForm] = useState({ ...footerContent });

  // User form
  const [showUserModal, setShowUserModal] = useState(false);
  const [userForm, setUserForm] = useState({ email: '', password: '', name: '', role: 'editor' });
  const [userError, setUserError] = useState('');
  const [userSuccess, setUserSuccess] = useState('');

  // Helper: file to base64
  const fileToBase64 = (file) => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result);
    reader.onerror = error => reject(error);
  });

  // Auth check
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        const adminQuery = query(usersCollection, where("email", "==", user.email), where("role", "in", ["super_admin", "admin"]));
        const adminSnap = await getDocs(adminQuery);
        if (!adminSnap.empty) {
          setIsAuthenticated(true);
          setCurrentAdmin({ uid: user.uid, ...adminSnap.docs[0].data() });
          await fetchData();
        } else {
          setIsAuthenticated(false);
          setCurrentAdmin(null);
          setLoading(false);
        }
      } else {
        setIsAuthenticated(false);
        setCurrentAdmin(null);
        setLoading(false);
      }
    });
    return () => unsubscribe();

    
  }, []);
  useEffect(() => {
      // Create particles
      for(let i = 0; i < 50; i++) {
        const particle = document.createElement('div');
        particle.classList.add('particle');
        particle.style.left = Math.random() * 100 + '%';
        particle.style.animationDelay = Math.random() * 8 + 's';
        particle.style.animationDuration = 5 + Math.random() * 5 + 's';
        particle.style.width = Math.random() * 3 + 1 + 'px';
        particle.style.height = particle.style.width;
        document.body.appendChild(particle);
      }
      
      return () => {
        document.querySelectorAll('.particle').forEach(p => p.remove());
      };
    }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      // Chapters
      const chaptersQuery = query(chaptersCollection, orderBy("order", "asc"));
      const chaptersSnap = await getDocs(chaptersQuery);
      setChapters(chaptersSnap.docs.map(d => ({ id: d.id, ...d.data() })));

      // Manifesto
      const manifestoQuery = query(manifestoLinesCollection, orderBy("order", "asc"));
      const manifestoSnap = await getDocs(manifestoQuery);
      setManifestoLines(manifestoSnap.docs.map(d => ({ id: d.id, ...d.data() })));

      // All sections - handle gracefully if collection doesn't exist
      let allSections = [];
      try {
        const sectionsSnap = await getDocs(sectionsCollection);
        allSections = sectionsSnap.docs.map(d => ({ id: d.id, ...d.data() }));
      } catch (err) {
        console.log("Sections collection may not exist yet, using defaults");
      }

      // Top Bar 1
      const tb1 = allSections.find(s => s.type === 'topbar1');
      if (tb1) setTopBar1Content(tb1.content || '');
      else setTopBar1Content('⚡ NEW SIGNAL: LATEST CHAPTERS AVAILABLE ⚡ FREE SHIPPING ON ARTIFACTS ABOVE ₹1499 ⚡ USE CODE: SKULL15 ⚡');

      // Top Bar 2
      const tb2 = allSections.find(s => s.type === 'topbar2');
      if (tb2) setTopBar2Content(tb2.content || '');
      else setTopBar2Content('⛧ EACH DROP DELIBERATE EACH PIECE A STATEMENT ⛧ IDENTITY SHOULD BE CONSTRUCTED. NOT INHERITED.');

      // Hero
      const hero = allSections.find(s => s.type === 'hero');
      if (hero) {
        setHeroContent({
          badge: hero.badge || heroContent.badge,
          title: hero.title || heroContent.title,
          titleHighlight: hero.titleHighlight || heroContent.titleHighlight,
          titleLine2: hero.titleLine2 || heroContent.titleLine2,
          subtitle: hero.subtitle || heroContent.subtitle,
          buttonPrimary: hero.buttonPrimary || heroContent.buttonPrimary,
          buttonSecondary: hero.buttonSecondary || heroContent.buttonSecondary,
          imageData: hero.imageData || ''
        });
      }

      // Footer
      const footer = allSections.find(s => s.type === 'footer');
      if (footer) {
        setFooterContent({
          tagline: footer.tagline || footerContent.tagline,
          contactEmail: footer.contactEmail || footerContent.contactEmail,
          contactLocation: footer.contactLocation || footerContent.contactLocation,
          copyright: footer.copyright || footerContent.copyright
        });
      }

      // About sections
      const aboutCards = allSections.filter(s => s.type === 'about').sort((a, b) => (a.order || 0) - (b.order || 0));
      if (aboutCards.length > 0) setAboutSections(aboutCards);
      else {
        // Default about sections
        setAboutSections([
          { id: 'default1', title: 'Our Philosophy', content: 'SKULLTURE was created in opposition to unconscious identity. We believe most people are shaped by systems they never intentionally chose.', icon: '⛧', order: 0 },
          { id: 'default2', title: 'The Mission', content: 'To create garments that serve as symbolic artifacts representing deliberate existence. Each drop is intentional. Each piece makes a statement.', icon: '✦', order: 1 },
          { id: 'default3', title: 'The Community', content: 'Join a collective of individuals who choose awareness over conformity. Initiation grants access to exclusive drops and hidden transmissions.', icon: '⟡', order: 2 },
          { id: 'default4', title: 'The Vision', content: 'To redefine fashion as a medium for self-construction rather than external validation.', icon: '◈', order: 3 }
        ]);
      }

      // Admin users
      const adminQ = query(usersCollection, where("role", "in", ["super_admin", "admin", "editor"]));
      const adminSnap = await getDocs(adminQ);
      setAdminUsers(adminSnap.docs.map(d => ({ id: d.id, ...d.data() })));

      // Regular users
      const regularQ = query(usersCollection, where("role", "==", "user"));
      const regularSnap = await getDocs(regularQ);
      setRegularUsers(regularSnap.docs.map(d => ({ id: d.id, ...d.data() })));

    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleAdminLogin = async (e) => {
    e.preventDefault();
    setLoginLoading(true);
    setLoginError('');
    try {
      const userCredential = await signInWithEmailAndPassword(auth, loginEmail, loginPassword);
      const user = userCredential.user;
      const adminQuery = query(usersCollection, where("email", "==", user.email), where("role", "in", ["super_admin", "admin"]));
      const adminSnap = await getDocs(adminQuery);
      if (adminSnap.empty) {
        setLoginError('⛧ Access denied. Admin privileges required. ⛧');
        await signOut(auth);
        setLoginLoading(false);
        return;
      }
      if (!user.emailVerified) {
        setLoginError('⛧ Please verify your email first ⛧');
        setLoginLoading(false);
        return;
      }
      setIsAuthenticated(true);
      setCurrentAdmin({ uid: user.uid, ...adminSnap.docs[0].data() });
      await fetchData();
    } catch (error) {
      let message = '⛧ Admin authentication failed ⛧';
      if (error.code === 'auth/user-not-found') message = '⛧ Admin account not found ⛧';
      if (error.code === 'auth/wrong-password') message = '⛧ Incorrect password ⛧';
      setLoginError(message);
    } finally {
      setLoginLoading(false);
    }
  };

  const handleAdminLogout = async () => {
    await signOut(auth);
    setIsAuthenticated(false);
    setCurrentAdmin(null);
  };

  // Create admin/editor user
  const handleCreateUser = async () => {
    if (!userForm.email || !userForm.password) { setUserError('Email and password are required'); return; }
    setUserError(''); setUserSuccess('');
    try {
      const userCredential = await createUserWithEmailAndPassword(auth, userForm.email, userForm.password);
      await sendEmailVerification(userCredential.user);
      await addDoc(usersCollection, {
        uid: userCredential.user.uid,
        email: userForm.email,
        name: userForm.name || userForm.email.split('@')[0],
        role: userForm.role,
        createdAt: new Date().toISOString(),
        createdBy: currentAdmin?.email
      });
      setUserSuccess(`User ${userForm.email} created successfully! Verification email sent.`);
      setUserForm({ email: '', password: '', name: '', role: 'editor' });
      await fetchData();
      setTimeout(() => { setShowUserModal(false); setUserSuccess(''); }, 2000);
    } catch (error) {
      let message = 'Failed to create user';
      if (error.code === 'auth/email-already-in-use') message = 'Email already in use';
      if (error.code === 'auth/weak-password') message = 'Password too weak (min 6 characters)';
      setUserError(message);
    }
  };

  const handleDeleteUser = async (userId, userEmail) => {
    if (window.confirm(`Are you sure you want to delete user: ${userEmail}?`)) {
      try {
        await deleteDoc(doc(db, "users", userId));
        await fetchData();
        alert(`User ${userEmail} removed from system`);
      } catch (error) { alert("Failed to delete user"); }
    }
  };

  const handleUpdateUserRole = async (userId, currentRole) => {
    const newRole = prompt(`Change role. Current: ${currentRole}\nEnter new role (super_admin/admin/editor/user):`, currentRole);
    if (newRole && ['super_admin', 'admin', 'editor', 'user'].includes(newRole)) {
      await updateDoc(doc(db, "users", userId), { role: newRole });
      await fetchData();
      alert(`User role updated to ${newRole}`);
    } else if (newRole) {
      alert('Invalid role. Use: super_admin, admin, editor, or user');
    }
  };

  // Chapter CRUD
  const handleAddChapter = async () => {
    if (!chapterForm.name) return;
    let imageData = '';
    if (chapterForm.imageFile) imageData = await fileToBase64(chapterForm.imageFile);
    const newChapter = {
      name: chapterForm.name,
      tag: chapterForm.tag || `CHAPTER ${chapters.length + 1}`,
      description: chapterForm.description,
      fullDescription: chapterForm.fullDescription || chapterForm.description,
      price: parseInt(chapterForm.price) || 499,
      status: chapterForm.status,
      order: parseInt(chapterForm.order) || chapters.length,
      imageData: imageData || chapterForm.imagePreview,
      createdAt: new Date().toISOString(),
      createdBy: currentAdmin?.email
    };
    if (editingChapter) {
      await updateDoc(doc(db, "chapters", editingChapter.id), newChapter);
    } else {
      await addDoc(chaptersCollection, newChapter);
    }
    await fetchData();
    setShowChapterModal(false);
    setEditingChapter(null);
    setChapterForm({ name: '', tag: '', description: '', fullDescription: '', status: 'active', order: 0, price: 499, imageFile: null, imagePreview: '', imageData: '' });
  };

  const handleEditChapter = (chapter) => {
    setEditingChapter(chapter);
    setChapterForm({
      name: chapter.name, tag: chapter.tag || '', description: chapter.description,
      fullDescription: chapter.fullDescription || '', price: chapter.price || 499,
      status: chapter.status, order: chapter.order,
      imageFile: null, imagePreview: chapter.imageData || '', imageData: chapter.imageData || ''
    });
    setShowChapterModal(true);
  };

  const handleDeleteChapter = async (id) => {
    if (window.confirm('Are you sure you want to delete this chapter?')) {
      await deleteDoc(doc(db, "chapters", id));
      await fetchData();
    }
  };

  const handleChapterImageChange = async (e) => {
    const file = e.target.files[0];
    if (file) {
      const preview = URL.createObjectURL(file);
      setChapterForm({ ...chapterForm, imageFile: file, imagePreview: preview });
    }
  };

  // Manifesto CRUD
  const handleAddManifesto = async () => {
    if (!manifestoForm.text) return;
    const newManifesto = {
      text: manifestoForm.text,
      order: parseInt(manifestoForm.order) || manifestoLines.length,
      createdBy: currentAdmin?.email
    };
    if (editingManifesto) {
      await updateDoc(doc(db, "manifestoLines", editingManifesto.id), newManifesto);
    } else {
      await addDoc(manifestoLinesCollection, newManifesto);
    }
    await fetchData();
    setShowManifestoModal(false);
    setEditingManifesto(null);
    setManifestoForm({ text: '', order: 0 });
  };

  const handleEditManifesto = (line) => {
    setEditingManifesto(line);
    setManifestoForm({ text: line.text, order: line.order });
    setShowManifestoModal(true);
  };

  const handleDeleteManifesto = async (id) => {
    if (window.confirm('Are you sure you want to delete this manifesto line?')) {
      await deleteDoc(doc(db, "manifestoLines", id));
      await fetchData();
    }
  };

  // About CRUD
  const handleAddAbout = async () => {
    if (!aboutForm.title) return;
    const newAbout = {
      title: aboutForm.title, content: aboutForm.content,
      type: 'about', icon: aboutForm.icon || '⛧',
      order: parseInt(aboutForm.order) || aboutSections.length,
      createdAt: new Date().toISOString(), createdBy: currentAdmin?.email
    };
    if (editingAbout) {
      await updateDoc(doc(db, "sections", editingAbout.id), newAbout);
    } else {
      await addDoc(sectionsCollection, newAbout);
    }
    await fetchData();
    setShowAboutModal(false);
    setEditingAbout(null);
    setAboutForm({ title: '', content: '', icon: '⛧', order: 0 });
  };

  const handleEditAbout = (section) => {
    setEditingAbout(section);
    setAboutForm({ title: section.title, content: section.content, icon: section.icon || '⛧', order: section.order });
    setShowAboutModal(true);
  };

  const handleDeleteAbout = async (id) => {
    if (window.confirm('Are you sure you want to delete this about card?')) {
      await deleteDoc(doc(db, "sections", id));
      await fetchData();
    }
  };

  // Helper: upsert a section by type
  const upsertSection = async (type, data) => {
    const q = query(sectionsCollection, where("type", "==", type));
    const snap = await getDocs(q);
    if (!snap.empty) {
      await updateDoc(doc(db, "sections", snap.docs[0].id), { ...data, updatedAt: new Date().toISOString(), updatedBy: currentAdmin?.email });
    } else {
      await addDoc(sectionsCollection, { type, ...data, createdAt: new Date().toISOString(), createdBy: currentAdmin?.email });
    }
  };

  // Top Bar 1
  const handleUpdateTopBar1 = async () => {
    if (!topBar1Text) return;
    await upsertSection('topbar1', { content: topBar1Text });
    setTopBar1Content(topBar1Text);
    setShowTopBar1Modal(false);
    alert('Top Bar 1 updated!');
    await fetchData();
  };

  // Top Bar 2
  const handleUpdateTopBar2 = async () => {
    if (!topBar2Text) return;
    await upsertSection('topbar2', { content: topBar2Text });
    setTopBar2Content(topBar2Text);
    setShowTopBar2Modal(false);
    alert('Top Bar 2 updated!');
    await fetchData();
  };

  // Hero image handler
  const handleHeroImageChange = async (e) => {
    const file = e.target.files[0];
    if (file) {
      const preview = URL.createObjectURL(file);
      setHeroImagePreview(preview);
      setHeroImageFile(file);
    }
  };

  // Hero
  const handleUpdateHero = async () => {
    let imageData = heroForm.imageData;
    if (heroImageFile) {
      imageData = await fileToBase64(heroImageFile);
    }
    await upsertSection('hero', {
      badge: heroForm.badge, title: heroForm.title,
      titleHighlight: heroForm.titleHighlight, titleLine2: heroForm.titleLine2,
      subtitle: heroForm.subtitle, buttonPrimary: heroForm.buttonPrimary,
      buttonSecondary: heroForm.buttonSecondary, imageData: imageData
    });
    setHeroContent({ ...heroForm, imageData: imageData });
    setShowHeroModal(false);
    setHeroImageFile(null);
    setHeroImagePreview('');
    alert('Hero content updated!');
    await fetchData();
  };

  // Footer
  const handleUpdateFooter = async () => {
    await upsertSection('footer', {
      tagline: footerForm.tagline, contactEmail: footerForm.contactEmail,
      contactLocation: footerForm.contactLocation, copyright: footerForm.copyright
    });
    setFooterContent(footerForm);
    setShowFooterModal(false);
    alert('Footer content updated!');
    await fetchData();
  };

  // ---- LOGIN SCREEN ----
  if (!isAuthenticated) {
    return (
      <div className="admin-login-page">
        <CustomCursor />
        <div className="admin-login-card">
          <div className="admin-login-icon"> <img src={logoImage} alt="SKULLTURE"
            onError={(e) => {
              e.target.style.display = 'none';
              const parent = e.target.parentElement;
              const span = document.createElement('span');
              span.className = 'logo-text-fallback';
              span.textContent = 'SKULLTURE';
              parent.insertBefore(span, e.target);
            }}
          /></div>
          <h2>ADMIN ACCESS</h2>
          <p>Restricted Area — Authorized Personnel Only</p>
          <form onSubmit={handleAdminLogin}>
            <div className="input-group">
              <input type="email" placeholder="admin@skullture.com" value={loginEmail}
                onChange={(e) => setLoginEmail(e.target.value)} required />
            </div>
            <div className="input-group">
              <input type="password" placeholder="Password" value={loginPassword}
                onChange={(e) => setLoginPassword(e.target.value)} required />
            </div>
            {loginError && <div className="error-msg">{loginError}</div>}
            <button type="submit" className="admin-login-btn" disabled={loginLoading}>
              {loginLoading ? 'AUTHENTICATING...' : '⟡ VERIFY ADMIN ⟡'}
            </button>
          </form>
          <div className="admin-hint"><strong>Admin Access:</strong> Contact super admin for credentials</div>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="admin-loading">
        <CustomCursor />
        <div className="loading-spinner">⛧</div>
        <p>LOADING ARCHIVE DATA...</p>
      </div>
    );
  }

  // ---- MAIN ADMIN ----
  return (
    <div className="admin-page">
      <CustomCursor />

      <div className="admin-header">
        <div className="header-inner">
          <div className="logo">
            <div className="logo-icon">⛧</div>
            <div className="logo-text">SKULLTURE ADMIN</div>
          </div>
          <div className="admin-info">
            <span className="admin-email">{currentAdmin?.email}</span>
            <span className={`admin-role-badge ${currentAdmin?.role}`}>{currentAdmin?.role}</span>
          </div>
          <div className="nav-links">
            <button className="nav-link" onClick={() => window.location.href = '/'}>← BACK TO SITE</button>
            <button className="logout-btn" onClick={handleAdminLogout}>⟡ LOGOUT ⟡</button>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="admin-tabs">
        <button className={`tab-btn ${activeTab === 'chapters' ? 'active' : ''}`} onClick={() => setActiveTab('chapters')}>📖 Chapters</button>
        <button className={`tab-btn ${activeTab === 'manifesto' ? 'active' : ''}`} onClick={() => setActiveTab('manifesto')}>📝 Manifesto</button>
        <button className={`tab-btn ${activeTab === 'about' ? 'active' : ''}`} onClick={() => setActiveTab('about')}>📄 About Cards</button>
        <button className={`tab-btn ${activeTab === 'hero' ? 'active' : ''}`} onClick={() => setActiveTab('hero')}>🎯 Hero Section</button>
        <button className={`tab-btn ${activeTab === 'topbar1' ? 'active' : ''}`} onClick={() => setActiveTab('topbar1')}>📢 Top Bar 1</button>
        <button className={`tab-btn ${activeTab === 'topbar2' ? 'active' : ''}`} onClick={() => setActiveTab('topbar2')}>📢 Top Bar 2</button>
        <button className={`tab-btn ${activeTab === 'footer' ? 'active' : ''}`} onClick={() => setActiveTab('footer')}>📌 Footer</button>
        <button className={`tab-btn ${activeTab === 'users' ? 'active' : ''}`} onClick={() => setActiveTab('users')}>👥 Admin Users</button>
        <button className={`tab-btn ${activeTab === 'regular-users' ? 'active' : ''}`} onClick={() => setActiveTab('regular-users')}>👤 Site Users</button>
      </div>

      <div className="admin-main">

        {/* ── CHAPTERS ── */}
        {activeTab === 'chapters' && (
          <div className="section-card">
            <div className="section-header">
              <div className="section-title">📖 Manage Chapters</div>
              <button className="add-btn" onClick={() => {
                setEditingChapter(null);
                setChapterForm({ name: '', tag: '', description: '', fullDescription: '', status: 'active', order: chapters.length, price: 499, imageFile: null, imagePreview: '', imageData: '' });
                setShowChapterModal(true);
              }}>+ New Chapter</button>
            </div>
            <p style={{ color: '#888', fontSize: '12px', marginBottom: '16px' }}>
              Chapters display in ORDER (lowest first). Even index = left side, odd index = right side on timeline.
            </p>
            <div className="table-container">
              <table className="data-table">
                <thead>
                  <tr><th>IMAGE</th><th>CHAPTER</th><th>TAG</th><th>PRICE</th><th>STATUS</th><th>ORDER</th><th>ACTIONS</th></tr>
                </thead>
                <tbody>
                  {chapters.map((chapter) => (
                    <tr key={chapter.id}>
                      <td>{chapter.imageData ? <img src={chapter.imageData} alt={chapter.name} style={{ width: '40px', height: '40px', objectFit: 'cover', borderRadius: '4px' }} /> : '📷'}</td>
                      <td>{chapter.name}</td>
                      <td><code>{chapter.tag}</code></td>
                      <td>₹{chapter.price || 499}</td>
                      <td><span className={`status-badge ${chapter.status === 'active' ? 'status-active' : 'status-draft'}`}>● {chapter.status?.toUpperCase()}</span></td>
                      <td>{chapter.order}</td>
                      <td className="action-btns">
                        <button className="action-btn edit" onClick={() => handleEditChapter(chapter)}>✎</button>
                        <button className="action-btn delete" onClick={() => handleDeleteChapter(chapter.id)}>🗑</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* ── MANIFESTO ── */}
        {activeTab === 'manifesto' && (
          <div className="section-card">
            <div className="section-header">
              <div className="section-title">📝 Manifesto Lines (rotates every 3 seconds)</div>
              <button className="add-btn" onClick={() => {
                setEditingManifesto(null);
                setManifestoForm({ text: '', order: manifestoLines.length });
                setShowManifestoModal(true);
              }}>+ New Line</button>
            </div>
            <div className="manifesto-list">
              {manifestoLines.map((line, idx) => (
                <div key={line.id} className="manifesto-list-item">
                  <div className="manifesto-order">{idx + 1}</div>
                  <div className="manifesto-text">{line.text}</div>
                  <div className="manifesto-actions">
                    <button className="action-btn edit" onClick={() => handleEditManifesto(line)}>✎</button>
                    <button className="action-btn delete" onClick={() => handleDeleteManifesto(line.id)}>🗑</button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ── ABOUT CARDS ── */}
        {activeTab === 'about' && (
          <div className="section-card">
            <div className="section-header">
              <div className="section-title">📄 About Section Cards</div>
              <button className="add-btn" onClick={() => {
                setEditingAbout(null);
                setAboutForm({ title: '', content: '', icon: '⛧', order: aboutSections.length });
                setShowAboutModal(true);
              }}>+ New Card</button>
            </div>
            <div className="table-container">
              <table className="data-table">
                <thead>
                  <tr><th>ICON</th><th>TITLE</th><th>CONTENT</th><th>ORDER</th><th>ACTIONS</th></tr>
                </thead>
                <tbody>
                  {aboutSections.map((section) => (
                    <tr key={section.id}>
                      <td><span style={{ fontSize: '24px' }}>{section.icon || '⛧'}</span></td>
                      <td>{section.title}</td>
                      <td style={{ color: '#888', fontSize: '13px' }}>{section.content?.substring(0, 80)}...</td>
                      <td>{section.order}</td>
                      <td className="action-btns">
                        <button className="action-btn edit" onClick={() => handleEditAbout(section)}>✎</button>
                        <button className="action-btn delete" onClick={() => handleDeleteAbout(section.id)}>🗑</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* ── HERO ── */}
        {activeTab === 'hero' && (
          <div className="section-card">
            <div className="section-header">
              <div className="section-title">🎯 Hero Section Content</div>
              <button className="add-btn" onClick={() => { setHeroForm({ ...heroContent }); setHeroImagePreview(heroContent.imageData || ''); setShowHeroModal(true); }}>✎ Edit Hero</button>
            </div>
            <div className="hero-preview">
              <h4>Current Hero Content:</h4>
              <div className="hero-preview-content">
                {heroContent.imageData && <div><strong>Hero Image:</strong> <img src={heroContent.imageData} alt="Hero" style={{ width: '60px', height: '60px', objectFit: 'cover', borderRadius: '8px' }} /></div>}
                <p><strong>Badge:</strong> {heroContent.badge}</p>
                <p><strong>Title:</strong> {heroContent.title} <span style={{ color: '#e11d1d' }}>{heroContent.titleHighlight}</span></p>
                <p><strong>Title Line 2:</strong> {heroContent.titleLine2}</p>
                <p><strong>Subtitle:</strong> {heroContent.subtitle}</p>
                <p><strong>Button Primary:</strong> {heroContent.buttonPrimary}</p>
                <p><strong>Button Secondary:</strong> {heroContent.buttonSecondary}</p>
              </div>
            </div>
          </div>
        )}

        {/* ── TOP BAR 1 ── */}
        {activeTab === 'topbar1' && (
          <div className="section-card">
            <div className="section-header">
              <div className="section-title">📢 Top Bar 1 (above the header)</div>
              <button className="add-btn" onClick={() => { setTopBar1Text(topBar1Content); setShowTopBar1Modal(true); }}>✎ Edit</button>
            </div>
            <div className="topbar-preview">
              <h4>Current Content:</h4>
              <div className="topbar-preview-content">{topBar1Content || '(empty)'}</div>
            </div>
          </div>
        )}

        {/* ── TOP BAR 2 ── */}
        {activeTab === 'topbar2' && (
          <div className="section-card">
            <div className="section-header">
              <div className="section-title">📢 Top Bar 2 (below hero)</div>
              <button className="add-btn" onClick={() => { setTopBar2Text(topBar2Content); setShowTopBar2Modal(true); }}>✎ Edit</button>
            </div>
            <div className="topbar-preview">
              <h4>Current Content:</h4>
              <div className="topbar-preview-content">{topBar2Content || '(empty)'}</div>
            </div>
          </div>
        )}

        {/* ── FOOTER ── */}
        {activeTab === 'footer' && (
          <div className="section-card">
            <div className="section-header">
              <div className="section-title">📌 Footer Content</div>
              <button className="add-btn" onClick={() => { setFooterForm({ ...footerContent }); setShowFooterModal(true); }}>✎ Edit Footer</button>
            </div>
            <div className="footer-preview">
              <h4>Current Footer Content:</h4>
              <div className="footer-preview-content">
                <p><strong>Tagline:</strong> {footerContent.tagline}</p>
                <p><strong>Contact Email:</strong> {footerContent.contactEmail}</p>
                <p><strong>Location:</strong> {footerContent.contactLocation}</p>
                <p><strong>Copyright:</strong> {footerContent.copyright}</p>
              </div>
            </div>
          </div>
        )}

        {/* ── ADMIN USERS ── */}
        {activeTab === 'users' && (
          <div className="section-card">
            <div className="section-header">
              <div className="section-title">👥 Admin & Staff Users</div>
              <button className="add-btn" onClick={() => { setShowUserModal(true); setUserError(''); setUserSuccess(''); }}>+ New Admin User</button>
            </div>
            <div className="table-container">
              <table className="data-table">
                <thead>
                  <tr><th>NAME</th><th>EMAIL</th><th>ROLE</th><th>CREATED</th><th>ACTIONS</th></tr>
                </thead>
                <tbody>
                  {adminUsers.map((user) => (
                    <tr key={user.id}>
                      <td>{user.name || user.email.split('@')[0]}</td>
                      <td>{user.email}</td>
                      <td><span className={`role-badge ${user.role}`}>{user.role}</span></td>
                      <td>{new Date(user.createdAt).toLocaleDateString()}</td>
                      <td className="action-btns">
                        <button className="action-btn edit" onClick={() => handleUpdateUserRole(user.id, user.role)}>✎ Role</button>
                        {user.role !== 'super_admin' && (
                          <button className="action-btn delete" onClick={() => handleDeleteUser(user.id, user.email)}>🗑</button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* ── REGULAR USERS ── */}
        {activeTab === 'regular-users' && (
          <div className="section-card">
            <div className="section-header">
              <div className="section-title">👤 Registered Site Users</div>
            </div>
            <div className="table-container">
              <table className="data-table">
                <thead>
                  <tr><th>NAME</th><th>EMAIL</th><th>ROLE</th><th>JOINED</th><th>ACTIONS</th></tr>
                </thead>
                <tbody>
                  {regularUsers.map((user) => (
                    <tr key={user.id}>
                      <td>{user.name || user.email.split('@')[0]}</td>
                      <td>{user.email}</td>
                      <td><span className="role-badge user">user</span></td>
                      <td>{new Date(user.createdAt).toLocaleDateString()}</td>
                      <td className="action-btns">
                        <button className="action-btn delete" onClick={() => handleDeleteUser(user.id, user.email)}>🗑</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      {/* ===== MODALS ===== */}

      {/* Chapter Modal */}
      {showChapterModal && (
        <div className="modal open" onClick={() => setShowChapterModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{editingChapter ? 'Edit Chapter' : 'Create New Chapter'}</h3>
              <button className="modal-close" onClick={() => setShowChapterModal(false)}>✕</button>
            </div>
            <div className="form-group"><label>CHAPTER NAME</label>
              <input type="text" value={chapterForm.name} onChange={(e) => setChapterForm({ ...chapterForm, name: e.target.value })} placeholder="Enter chapter name" /></div>
            <div className="form-group"><label>TAG</label>
              <input type="text" value={chapterForm.tag} onChange={(e) => setChapterForm({ ...chapterForm, tag: e.target.value })} placeholder="/// CHAPTER 01 ///" /></div>
            <div className="form-group"><label>DESCRIPTION</label>
              <textarea rows="3" value={chapterForm.description} onChange={(e) => setChapterForm({ ...chapterForm, description: e.target.value })} placeholder="Brief description"></textarea></div>
            <div className="form-group"><label>FULL DESCRIPTION</label>
              <textarea rows="4" value={chapterForm.fullDescription} onChange={(e) => setChapterForm({ ...chapterForm, fullDescription: e.target.value })} placeholder="Detailed description"></textarea></div>
            <div className="form-row">
              <div className="form-group"><label>PRICE (₹)</label>
                <input type="number" value={chapterForm.price} onChange={(e) => setChapterForm({ ...chapterForm, price: e.target.value })} /></div>
              <div className="form-group"><label>ORDER (0 = first)</label>
                <input type="number" value={chapterForm.order} onChange={(e) => setChapterForm({ ...chapterForm, order: e.target.value })} /></div>
              <div className="form-group"><label>STATUS</label>
                <select value={chapterForm.status} onChange={(e) => setChapterForm({ ...chapterForm, status: e.target.value })}>
                  <option value="active">Active</option><option value="draft">Draft</option>
                </select></div>
            </div>
            <div className="form-group"><label>CHAPTER IMAGE</label>
              <input type="file" accept="image/*" onChange={handleChapterImageChange} /></div>
            {chapterForm.imagePreview && <img src={chapterForm.imagePreview} alt="Preview" style={{ width: '100px', marginTop: '10px', borderRadius: '8px' }} />}
            <button className="submit-btn" onClick={handleAddChapter}>{editingChapter ? 'UPDATE CHAPTER' : 'CREATE CHAPTER'}</button>
          </div>
        </div>
      )}

      {/* Manifesto Modal */}
      {showManifestoModal && (
        <div className="modal open" onClick={() => setShowManifestoModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{editingManifesto ? 'Edit Manifesto Line' : 'Add Manifesto Line'}</h3>
              <button className="modal-close" onClick={() => setShowManifestoModal(false)}>✕</button>
            </div>
            <div className="form-group"><label>MANIFESTO TEXT</label>
              <input type="text" value={manifestoForm.text} onChange={(e) => setManifestoForm({ ...manifestoForm, text: e.target.value })} placeholder="Enter manifesto line" /></div>
            <div className="form-group"><label>ORDER</label>
              <input type="number" value={manifestoForm.order} onChange={(e) => setManifestoForm({ ...manifestoForm, order: e.target.value })} /></div>
            <button className="submit-btn" onClick={handleAddManifesto}>{editingManifesto ? 'UPDATE' : 'ADD'}</button>
          </div>
        </div>
      )}

      {/* About Modal */}
      {showAboutModal && (
        <div className="modal open" onClick={() => setShowAboutModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{editingAbout ? 'Edit About Card' : 'Add About Card'}</h3>
              <button className="modal-close" onClick={() => setShowAboutModal(false)}>✕</button>
            </div>
            <div className="form-group"><label>TITLE</label>
              <input type="text" value={aboutForm.title} onChange={(e) => setAboutForm({ ...aboutForm, title: e.target.value })} placeholder="Card title" /></div>
            <div className="form-group"><label>ICON (emoji)</label>
              <input type="text" value={aboutForm.icon} onChange={(e) => setAboutForm({ ...aboutForm, icon: e.target.value })} placeholder="⛧" /></div>
            <div className="form-group"><label>CONTENT</label>
              <textarea rows="4" value={aboutForm.content} onChange={(e) => setAboutForm({ ...aboutForm, content: e.target.value })} placeholder="Card content"></textarea></div>
            <div className="form-group"><label>ORDER</label>
              <input type="number" value={aboutForm.order} onChange={(e) => setAboutForm({ ...aboutForm, order: e.target.value })} /></div>
            <button className="submit-btn" onClick={handleAddAbout}>{editingAbout ? 'UPDATE' : 'ADD'}</button>
          </div>
        </div>
      )}

      {/* Hero Modal */}
      {showHeroModal && (
        <div className="modal open" onClick={() => setShowHeroModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Edit Hero Section</h3>
              <button className="modal-close" onClick={() => setShowHeroModal(false)}>✕</button>
            </div>
            <div className="form-group"><label>HERO IMAGE</label>
              <input type="file" accept="image/*" onChange={handleHeroImageChange} />
              {(heroImagePreview || heroForm.imageData) && (
                <img src={heroImagePreview || heroForm.imageData} alt="Hero Preview" style={{ width: '100px', marginTop: '10px', borderRadius: '8px' }} />
              )}
            </div>
            <div className="form-group"><label>BADGE TEXT</label>
              <input type="text" value={heroForm.badge} onChange={(e) => setHeroForm({ ...heroForm, badge: e.target.value })} placeholder="/// CHAPTER ZERO ///" /></div>
            <div className="form-group"><label>TITLE (Part 1)</label>
              <input type="text" value={heroForm.title} onChange={(e) => setHeroForm({ ...heroForm, title: e.target.value })} placeholder="WEAR THE" /></div>
            <div className="form-group"><label>TITLE HIGHLIGHT (red text)</label>
              <input type="text" value={heroForm.titleHighlight} onChange={(e) => setHeroForm({ ...heroForm, titleHighlight: e.target.value })} placeholder="OCCULT" /></div>
            <div className="form-group"><label>TITLE LINE 2</label>
              <input type="text" value={heroForm.titleLine2} onChange={(e) => setHeroForm({ ...heroForm, titleLine2: e.target.value })} placeholder="STRUCTURE" /></div>
            <div className="form-group"><label>SUBTITLE</label>
              <textarea rows="2" value={heroForm.subtitle} onChange={(e) => setHeroForm({ ...heroForm, subtitle: e.target.value })} placeholder="Restricted archive..."></textarea></div>
            <div className="form-group"><label>PRIMARY BUTTON TEXT</label>
              <input type="text" value={heroForm.buttonPrimary} onChange={(e) => setHeroForm({ ...heroForm, buttonPrimary: e.target.value })} placeholder="EXPLORE CHAPTERS" /></div>
            <div className="form-group"><label>SECONDARY BUTTON TEXT</label>
              <input type="text" value={heroForm.buttonSecondary} onChange={(e) => setHeroForm({ ...heroForm, buttonSecondary: e.target.value })} placeholder="VIEW MANIFESTO" /></div>
            <button className="submit-btn" onClick={handleUpdateHero}>UPDATE HERO</button>
          </div>
        </div>
      )}

      {/* Top Bar 1 Modal */}
      {showTopBar1Modal && (
        <div className="modal open" onClick={() => setShowTopBar1Modal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Edit Top Bar 1 (above header)</h3>
              <button className="modal-close" onClick={() => setShowTopBar1Modal(false)}>✕</button>
            </div>
            <div className="form-group"><label>SCROLLING TEXT</label>
              <textarea rows="3" value={topBar1Text} onChange={(e) => setTopBar1Text(e.target.value)} placeholder="Enter scrolling text..."></textarea></div>
            <button className="submit-btn" onClick={handleUpdateTopBar1}>UPDATE TOP BAR 1</button>
          </div>
        </div>
      )}

      {/* Top Bar 2 Modal */}
      {showTopBar2Modal && (
        <div className="modal open" onClick={() => setShowTopBar2Modal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Edit Top Bar 2 (below hero)</h3>
              <button className="modal-close" onClick={() => setShowTopBar2Modal(false)}>✕</button>
            </div>
            <div className="form-group"><label>SCROLLING TEXT</label>
              <textarea rows="3" value={topBar2Text} onChange={(e) => setTopBar2Text(e.target.value)} placeholder="Enter scrolling text..."></textarea></div>
            <button className="submit-btn" onClick={handleUpdateTopBar2}>UPDATE TOP BAR 2</button>
          </div>
        </div>
      )}

      {/* Footer Modal */}
      {showFooterModal && (
        <div className="modal open" onClick={() => setShowFooterModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Edit Footer Content</h3>
              <button className="modal-close" onClick={() => setShowFooterModal(false)}>✕</button>
            </div>
            <div className="form-group"><label>TAGLINE</label>
              <input type="text" value={footerForm.tagline} onChange={(e) => setFooterForm({ ...footerForm, tagline: e.target.value })} /></div>
            <div className="form-group"><label>CONTACT EMAIL</label>
              <input type="email" value={footerForm.contactEmail} onChange={(e) => setFooterForm({ ...footerForm, contactEmail: e.target.value })} /></div>
            <div className="form-group"><label>LOCATION</label>
              <input type="text" value={footerForm.contactLocation} onChange={(e) => setFooterForm({ ...footerForm, contactLocation: e.target.value })} /></div>
            <div className="form-group"><label>COPYRIGHT TEXT</label>
              <input type="text" value={footerForm.copyright} onChange={(e) => setFooterForm({ ...footerForm, copyright: e.target.value })} /></div>
            <button className="submit-btn" onClick={handleUpdateFooter}>UPDATE FOOTER</button>
          </div>
        </div>
      )}

      {/* User Modal */}
      {showUserModal && (
        <div className="modal open" onClick={() => setShowUserModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Create New Admin/Staff User</h3>
              <button className="modal-close" onClick={() => setShowUserModal(false)}>✕</button>
            </div>
            {userError && <div className="error-msg" style={{ marginBottom: '15px' }}>{userError}</div>}
            {userSuccess && <div className="success-msg" style={{ marginBottom: '15px' }}>{userSuccess}</div>}
            <div className="form-group"><label>FULL NAME</label>
              <input type="text" value={userForm.name} onChange={(e) => setUserForm({ ...userForm, name: e.target.value })} placeholder="Enter full name" /></div>
            <div className="form-group"><label>EMAIL ADDRESS</label>
              <input type="email" value={userForm.email} onChange={(e) => setUserForm({ ...userForm, email: e.target.value })} placeholder="user@skullture.com" /></div>
            <div className="form-group"><label>PASSWORD</label>
              <input type="password" value={userForm.password} onChange={(e) => setUserForm({ ...userForm, password: e.target.value })} placeholder="Min 6 characters" /></div>
            <div className="form-group"><label>ROLE</label>
              <select value={userForm.role} onChange={(e) => setUserForm({ ...userForm, role: e.target.value })}>
                <option value="super_admin">Super Admin</option>
                <option value="admin">Admin</option>
                <option value="editor">Editor</option>
              </select></div>
            <button className="submit-btn" onClick={handleCreateUser}>CREATE USER</button>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminPage;